import CobraSpinner from './CobraSpinner';
import React, { useState } from 'react';
import { 
  Button, 
  FormControl, 
  FormLabel, 
  Input, 
  Textarea,
  VStack, 
  HStack,
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalFooter, 
  ModalBody, 
  ModalCloseButton, 
  useToast,
  Box,
  Text,
  Badge,
  
  Icon,
  Tooltip,
  useColorModeValue,
  ScaleFade,
  SlideFade,
  useBreakpointValue} from '@chakra-ui/react';
import { FaLayerGroup, FaPlus, FaSave, FaTimes, FaFileAlt, FaBookmark } from 'react-icons/fa';
import axiosInstance from '../services/axiosInstance';

const AddPartieForm = ({ isOpen, courseId, chapterId, onClose, onPartieAdded }) => {
  const [nomPartie, setNomPartie] = useState('');
  const [descriptionPartie, setDescriptionPartie] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Responsive values
  const modalSize = useBreakpointValue({ base: "full", md: "xl", lg: "2xl" });
  const headerSize = useBreakpointValue({ base: "lg", md: "xl", lg: "2xl" });
  const buttonSize = useBreakpointValue({ base: "md", md: "lg" });

  // Color mode values
  const bgGradient = useColorModeValue(
    'linear(135deg, teal.500 0%, green.600 50%, blue.500 100%)',
    'linear(135deg, teal.600 0%, green.700 50%, blue.600 100%)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const primaryColor = '#032D7F';

  const handleAjouterPartie = async () => {
    if (!nomPartie.trim()) {
      toast({
        title: "خطأ في البيانات ❌",
        description: "اسم الجزء مطلوب",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-left"
      });
      return;
    }

    try {
      setLoading(true);
      const nouvellePartie = {
        name: nomPartie.trim(),
        description: descriptionPartie.trim() || `جزء جديد في الفصل`,
        videos: [], 
        attachments: [] 
      };

      const response = await axiosInstance.post(
        `courses/courses/${courseId}/chapitres/${chapterId}/parties`, 
        nouvellePartie
      );

      // Success animation delay
      setTimeout(() => {
        toast({
          title: "تم الحفظ بنجاح! 🎉",
          description: `تم إضافة الجزء "${nomPartie}" بنجاح`,
          status: "success",
          duration: 4000,
          isClosable: true,
          position: "top-left"
        });

        resetForm();
        onPartieAdded();
        onClose();
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('خطأ أثناء إضافة الجزء:', error);
      setLoading(false);
      toast({
        title: "فشل في الحفظ ❌",
        description: error.response?.data?.message || "تعذر إضافة الجزء",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-left"
      });
    }
  };

  const resetForm = () => {
    setNomPartie('');
    setDescriptionPartie('');
    setLoading(false);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      isCentered 
      motionPreset='slideInBottom'
      size={modalSize}
      closeOnOverlayClick={!loading}
    >
      <ModalOverlay 
        backdropFilter="blur(15px)" 
        bg="blackAlpha.600"
        animation="fadeIn 0.3s ease-out"
      />
      <ModalContent 
        dir="rtl"
        borderRadius="3xl" 
        overflow="hidden" 
        shadow="2xl"
        bg={cardBg}
        border="1px solid"
        borderColor="whiteAlpha.200"
        transform={loading ? "scale(1.02)" : "scale(1)"}
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      >
        <ModalHeader
          bgGradient={bgGradient}
          color="white"
          textAlign="center"
          py={10}
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgGradient: "linear(45deg, transparent 30%, whiteAlpha.100 50%, transparent 70%)",
            transform: loading ? "translateX(100%)" : "translateX(-100%)",
            transition: "transform 1s ease-in-out",
          }}
        >
          <VStack spacing={4} position="relative" zIndex={1}>
            <Box
              p={4}
              bg="whiteAlpha.200"
              borderRadius="2xl"
              backdropFilter="blur(10px)"
              border="1px solid"
              borderColor="whiteAlpha.300"
              transform={loading ? "rotate(360deg) scale(1.1)" : "rotate(0deg) scale(1)"}
              transition="all 0.8s cubic-bezier(0.4, 0, 0.2, 1)"
            >
              <FaLayerGroup size={40} />
            </Box>
            <VStack spacing={2}>
              <Text 
                fontSize={headerSize} 
                fontWeight="bold" 
                textShadow="0 2px 4px rgba(0,0,0,0.3)"
              >
                {loading ? "جاري إضافة الجزء..." : "إضافة جزء جديد"}
              </Text>
              <HStack spacing={2}>
                <Icon as={FaBookmark} />
                <Text 
                  fontSize="md" 
                  opacity={0.9}
                  textAlign="center"
                >
                  إضافة جزء جديد للفصل
                </Text>
              </HStack>
            </VStack>
          </VStack>
        </ModalHeader>
        
        <ModalCloseButton 
          color="white" 
          size="lg"
          isDisabled={loading}
          _hover={{
            bg: "whiteAlpha.200",
            transform: "scale(1.1)"
          }}
          transition="all 0.2s ease"
        />

        <ModalBody p={{ base: 6, md: 10 }}>
          <VStack spacing={8}>
            {/* Part Name */}
            <ScaleFade in={isOpen} delay={0.1}>
              <FormControl isRequired>
                <FormLabel 
                  color={textColor} 
                  fontWeight="bold"
                  fontSize="lg"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <Box w={2} h={2} bg="teal.500" borderRadius="full" />
                  اسم الجزء
                  <Badge colorScheme="red" fontSize="xs" px={2} py={1} borderRadius="full">
                    مطلوب
                  </Badge>
                </FormLabel>
                <Input
                  value={nomPartie}
                  onChange={(e) => setNomPartie(e.target.value)}
                  placeholder="أدخل اسم الجزء"
                  dir="rtl"
                  borderRadius="2xl"
                  borderWidth="2px"
                  borderColor={nomPartie.trim() ? "green.300" : "gray.300"}
                  bg={nomPartie.trim() ? "green.50" : "gray.50"}
                  py={6}
                  fontSize="lg"
                  _focus={{
                    borderColor: primaryColor,
                    boxShadow: `0 0 0 3px ${primaryColor}20`,
                    bg: "white",
                    transform: "translateY(-2px)"
                  }}
                  _hover={{
                    borderColor: "gray.400",
                    bg: "white"
                  }}
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  isDisabled={loading}
                />
              </FormControl>
            </ScaleFade>

            {/* Part Description */}
            <SlideFade in={isOpen} offsetY="20px" delay={0.2}>
              <FormControl>
                <FormLabel 
                  color={textColor} 
                  fontWeight="bold"
                  fontSize="lg"
                  mb={3}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <Box w={2} h={2} bg="blue.500" borderRadius="full" />
                  وصف الجزء
                  <Badge colorScheme="gray" fontSize="xs" px={2} py={1} borderRadius="full">
                    اختياري
                  </Badge>
                </FormLabel>
                <Textarea
                  value={descriptionPartie}
                  onChange={(e) => setDescriptionPartie(e.target.value)}
                  placeholder="أدخل وصف مفصل للجزء (اختياري)"
                  dir="rtl"
                  borderRadius="2xl"
                  borderWidth="2px"
                  borderColor="gray.300"
                  bg="gray.50"
                  py={4}
                  fontSize="lg"
                  rows={4}
                  resize="vertical"
                  _focus={{
                    borderColor: primaryColor,
                    boxShadow: `0 0 0 3px ${primaryColor}20`,
                    bg: "white",
                    transform: "translateY(-2px)"
                  }}
                  _hover={{
                    borderColor: "gray.400",
                    bg: "white"
                  }}
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  isDisabled={loading}
                />
              </FormControl>
            </SlideFade>

            {/* Info Box */}
            <SlideFade in={isOpen} offsetY="20px" delay={0.3}>
              <Box
                p={6}
                bg="blue.50"
                borderRadius="2xl"
                border="2px solid"
                borderColor="blue.200"
                w="full"
              >
                <HStack spacing={3} mb={3}>
                  <Icon as={FaFileAlt} color="blue.500" />
                  <Text fontWeight="bold" color="blue.700">
                    معلومات مهمة
                  </Text>
                </HStack>
                <VStack align="start" spacing={2}>
                  <Text fontSize="sm" color="blue.600">
                    • سيتم إنشاء الجزء فارغاً ويمكنك إضافة الفيديوهات والمرفقات لاحقاً
                  </Text>
                  <Text fontSize="sm" color="blue.600">
                    • يمكنك تعديل اسم ووصف الجزء في أي وقت
                  </Text>
                  <Text fontSize="sm" color="blue.600">
                    • الجزء سيظهر في نهاية قائمة الأجزاء للفصل
                  </Text>
                </VStack>
              </Box>
            </SlideFade>

            {/* Loading State */}
            {loading && (
              <ScaleFade in={loading}>
                <VStack spacing={4} py={6}>
                  <CobraSpinner
                    size="xl"
                    color="teal.500"
                    thickness="4px"
                  />
                  <Text color={textColor} fontSize="lg" fontWeight="medium">
                    جاري إضافة الجزء...
                  </Text>
                  <Text fontSize="sm" color="gray.600" textAlign="center">
                    يرجى الانتظار حتى اكتمال العملية
                  </Text>
                </VStack>
              </ScaleFade>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter p={{ base: 6, md: 10 }} pt={0}>
          <HStack spacing={4} w="full" justify="center">
            <Tooltip label="إلغاء العملية" placement="top">
              <Button 
                variant="outline"
                onClick={handleClose}
                borderRadius="2xl"
                borderWidth="2px"
                px={8}
                py={6}
                fontSize="lg"
                isDisabled={loading}
                leftIcon={<FaTimes />}
                _hover={{
                  bg: "gray.100",
                  transform: loading ? "none" : "translateY(-2px)",
                  shadow: "lg"
                }}
                transition="all 0.3s ease"
              >
                إلغاء
              </Button>
            </Tooltip>
            
            <Tooltip label="حفظ الجزء الجديد" placement="top">
              <Button 
                bgGradient="linear(135deg, teal.500 0%, green.600 50%, blue.500 100%)"
                color="white"
                onClick={handleAjouterPartie} 
                isLoading={loading}
                loadingText="جاري الحفظ..."
                isDisabled={!nomPartie.trim() || loading}
                borderRadius="2xl"
                px={8}
                py={6}
                fontSize="lg"
                fontWeight="bold"
                leftIcon={!loading ? <FaSave /> : undefined}
                _hover={{
                  bgGradient: "linear(135deg, teal.600 0%, green.700 50%, blue.600 100%)",
                  transform: loading ? "none" : "translateY(-2px)",
                  shadow: "2xl"
                }}
                _active={{
                  transform: "scale(0.98)"
                }}
                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              >
                {loading ? "جاري الحفظ..." : "إضافة الجزء"}
              </Button>
            </Tooltip>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddPartieForm;


